import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SparklesIcon, UploadCloudIcon, GlobeIcon, PlusIcon, ArrowUpIcon, CheckCircle2Icon, ImageIcon, AlertCircleIcon, FileSpreadsheetIcon, LinkIcon, TagIcon, PencilIcon } from 'lucide-react';
type MessageType = 'greeting' | 'upload_prompt' | 'upload_processing' | 'upload_result' | 'url_prompt' | 'url_processing' | 'url_result' | 'single_result' | 'image_prompt' | 'excel_summary' | 'text';
type FlowStep = 'idle' | 'awaiting_single_desc' | 'awaiting_vehicle_details' | 'awaiting_vehicle_images' | 'awaiting_excel_context' | 'awaiting_excel_rules';
interface DraftItem {
  title: string;
  price: string;
  description: string;
}
interface Message {
  id: string;
  role: 'ai' | 'user';
  content: string;
  type: MessageType;
  draft?: DraftItem;
}
interface CatalogChatProps {
  catalogName: string;
  onPopulateDemo: () => void;
}
export function CatalogChat({
  catalogName,
  onPopulateDemo
}: CatalogChatProps) {
  const [messages, setMessages] = useState<Message[]>([{
    id: '1',
    role: 'ai',
    content: `Hi 👋 I'm Talab AI. Let's build your ${catalogName}. How would you like to start?`,
    type: 'greeting'
  }]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [flowStep, setFlowStep] = useState<FlowStep>('idle');
  const [draftItem, setDraftItem] = useState<DraftItem | null>(null);
  const [vehicleDetails, setVehicleDetails] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, [messages, isTyping]);
  const addMessage = (role: 'ai' | 'user', content: string, type: MessageType = 'text', draft?: DraftItem) => {
    setMessages(prev => [...prev, {
      id: Date.now().toString() + Math.random().toString(36).slice(2, 6),
      role,
      content,
      type,
      draft
    }]);
  };
  // Knowledge base for technical questions
  const answerQuestion = (text: string): string => {
    const t = text.toLowerCase();
    if (t.includes('publish')) {
      return 'When you publish, Talab structures each listing, writes an optimized title and description, and makes it discoverable in AI search and customer chat. You can unpublish anytime.';
    }
    if (t.includes('photo') || t.includes('picture') || t.includes('image')) {
      return 'Photos help your listings match better and build buyer trust. Items without photos stay as drafts until you add one — or you can publish without photos and add them later.';
    }
    if (t.includes('sync') || t.includes('website') || t.includes('often')) {
      return 'I crawl your website and keep it in sync daily. You can also trigger a manual re-sync anytime, and I only import items I can read cleanly.';
    }
    if (t.includes('api') || t.includes('integration') || t.includes('erp')) {
      return 'I support REST APIs and most ERPs for real-time inventory sync. I just need your endpoint and an API key — all credentials are encrypted.';
    }
    if (t.includes('data') || t.includes('safe') || t.includes('secure') || t.includes('privacy')) {
      return 'Your data is encrypted and only used to power your own catalog and AI assistant. It is never shared with other businesses.';
    }
    if (t.includes('excel') || t.includes('csv') || t.includes('format') || t.includes('column')) {
      return 'I accept .xlsx, .xls, and .csv files. There is no strict template — I automatically detect and map your columns to the catalog format.';
    }
    return null as unknown as string;
  };
  const buildVehicleDraft = (details: string): DraftItem => {
    const title = details.length > 48 ? details.slice(0, 48).trim() + '…' : details;
    return {
      title: title.charAt(0).toUpperCase() + title.slice(1),
      price: 'AED — / day',
      description: `AI-optimized vehicle listing generated from the details you shared (${details}). I've highlighted the make, model, year, and mileage so it ranks well in search and customer chat.`
    };
  };
  const finishVehicleListing = (hasImages: boolean) => {
    setIsTyping(true);
    setTimeout(() => {
      setIsTyping(false);
      const draft = buildVehicleDraft(vehicleDetails);
      setDraftItem(draft);
      addMessage('ai', hasImages ? "Perfect — I've added your photos. Here's the draft listing for your vehicle. Review it and choose what to do next:" : "No problem, you can add photos later. Here's the draft listing for your vehicle:", 'single_result', draft);
      setFlowStep('idle');
    }, 1300);
  };
  const handleVehicleImages = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    const count = files.length;
    addMessage('user', `Attached ${count} ${count === 1 ? 'photo' : 'photos'}`);
    e.target.value = '';
    finishVehicleListing(true);
  };
  const handleSend = () => {
    if (!inputValue.trim()) return;
    const text = inputValue.trim();
    setInputValue('');
    addMessage('user', text);
    // Excel flow: capture what the list is for
    if (flowStep === 'awaiting_excel_context') {
      const ctx = text.toLowerCase();
      const isRental = /\b(rent|rental|hire|lease|per day|daily)\b/.test(ctx);
      setFlowStep('awaiting_excel_rules');
      setIsTyping(true);
      setTimeout(() => {
        setIsTyping(false);
        addMessage('ai', isRental ? 'Perfect — rentals. I can see a price per day and a price per month in your sheet. Are there any other fees or rules you want to add (e.g. delivery fee, drop-off fee, child seat, insurance)?' : 'Got it. I can see your pricing columns. Are there any other fees or rules you want to add (e.g. delivery, deposit, or any add-ons)?');
      }, 1100);
      return;
    }
    // Excel flow: capture fees/rules, then show the import summary
    if (flowStep === 'awaiting_excel_rules') {
      setFlowStep('idle');
      setIsTyping(true);
      setTimeout(() => {
        setIsTyping(false);
        addMessage('ai', "Done — I've applied those fees and rules to all matching items, and automatically categorized them based on their descriptions. Here's a summary of your import:", 'excel_summary');
      }, 1300);
      return;
    }
    // Vehicle sell flow: capture details, then ask for images
    if (flowStep === 'awaiting_vehicle_details') {
      setVehicleDetails(text);
      setFlowStep('awaiting_vehicle_images');
      setIsTyping(true);
      setTimeout(() => {
        setIsTyping(false);
        addMessage('ai', "Great, I've got the make, model, year, and mileage. Now please share a few photos of the vehicle — clear shots of the exterior, interior, and dashboard help your listing perform best.", 'image_prompt');
      }, 1100);
      return;
    }
    // Detect "sell my car / vehicle" intent
    const t = text.toLowerCase();
    const sellVehicle = /\b(sell|list|add|post)\b/.test(t) && /\b(car|vehicle|truck|suv|motorcycle|motorbike|auto)\b/.test(t);
    if (flowStep === 'idle' && sellVehicle) {
      setIsTyping(true);
      setTimeout(() => {
        setIsTyping(false);
        addMessage('ai', "I'd be happy to help you list your vehicle. To get started, please share the vehicle details — Make, Model, Year, and Mileage. You can type them all in one message (e.g. 'Toyota Prado, 2018, 95,000 km').");
        setFlowStep('awaiting_vehicle_details');
      }, 1000);
      return;
    }
    // Single-item flow: capturing the item description
    if (flowStep === 'awaiting_single_desc') {
      setFlowStep('idle');
      setIsTyping(true);
      setTimeout(() => {
        setIsTyping(false);
        // Build a simple draft from the description
        const title = text.length > 48 ? text.slice(0, 48).trim() + '…' : text;
        const draft: DraftItem = {
          title: title.charAt(0).toUpperCase() + title.slice(1),
          price: 'AED — / day',
          description: `Well-presented listing generated from your description. I've written an AI-optimized summary highlighting the key details of "${text}" so it performs well in search and customer chat.`
        };
        setDraftItem(draft);
        addMessage('ai', "Here's the draft listing I prepared from your description. Review it and choose what to do next:", 'single_result', draft);
      }, 1300);
      return;
    }
    // Otherwise: answer technical questions from the knowledge base
    setIsTyping(true);
    setTimeout(() => {
      setIsTyping(false);
      const answer = answerQuestion(text);
      addMessage('ai', answer || "I can help with that. Whenever you're ready to add inventory, you can upload a file, connect your website, or add a single item — just pick an option or describe what you sell.");
    }, 1000);
  };
  const handleAction = (action: string) => {
    if (action === 'excel') {
      addMessage('user', 'Upload an Excel sheet');
      setIsTyping(true);
      setTimeout(() => {
        setIsTyping(false);
        addMessage('ai', 'Great! Please select your Excel (.xlsx) or CSV file. I will automatically map your columns to the catalog format.', 'upload_prompt');
      }, 800);
    } else if (action === 'api') {
      addMessage('user', 'Connect website / API integration');
      setIsTyping(true);
      setTimeout(() => {
        setIsTyping(false);
        addMessage('ai', 'Perfect. Please enter your website URL or API endpoint, and I will scan and import your inventory automatically.', 'url_prompt');
      }, 800);
    } else if (action === 'single') {
      addMessage('user', 'Add my first item');
      setIsTyping(true);
      setTimeout(() => {
        setIsTyping(false);
        addMessage('ai', "Let's create your first listing. Describe the item in your own words — include the name, and any details like price, year, or condition (e.g. 'Toyota Prado 2018, GCC specs, AED 350 per day').");
        setFlowStep('awaiting_single_desc');
      }, 700);
    }
  };
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    addMessage('user', `Attached: ${file.name}`);
    setIsTyping(true);
    setTimeout(() => {
      setIsTyping(false);
      addMessage('ai', 'Analyzing your file and mapping the columns...', 'upload_processing');
      setTimeout(() => {
        setIsTyping(false);
        addMessage('ai', 'Got it — I can read your list and have automatically categorized the items. What is this list for? For example, are these cars for rent, products for sale, or something else?');
        setFlowStep('awaiting_excel_context');
      }, 1500);
    }, 1000);
  };
  const handleUrlSubmit = (url: string) => {
    if (!url.trim()) return;
    addMessage('user', `Connect to: ${url}`);
    setIsTyping(true);
    setTimeout(() => {
      setIsTyping(false);
      addMessage('ai', 'Connecting to your site and syncing inventory...', 'url_processing');
      setTimeout(() => {
        addMessage('ai', 'Sync complete! I have automatically categorized your items based on their details. Here is the summary of your integration:', 'url_result');
      }, 2000);
    }, 1000);
  };
  const handlePublishAction = (action: string) => {
    addMessage('user', action);
    setIsTyping(true);
    setTimeout(() => {
      setIsTyping(false);
      addMessage('ai', 'Done! Your catalog has been updated. You can view your active inventory now.');
      setTimeout(() => {
        onPopulateDemo();
      }, 1500);
    }, 1000);
  };
  const handleEditDraft = () => {
    addMessage('user', 'Edit the details');
    setIsTyping(true);
    setTimeout(() => {
      setIsTyping(false);
      addMessage('ai', 'No problem — just tell me what to change (e.g. the price, title, or any detail) and I will update the draft.');
      setFlowStep('awaiting_single_desc');
    }, 800);
  };
  return <div className="flex flex-col flex-1 h-full min-h-[420px] bg-slate-50/50 rounded-3xl border border-slate-200 overflow-hidden relative" data-id="element-956" data-mp="3qg83z27nz6vp">
      {/* Chat Area */}
      <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto p-6 space-y-6" data-id="element-957" data-mp="3hhwfb5qcyj5w">
        <AnimatePresence initial={false} data-id="element-958" data-mp="1wz2dtvbaw2pn">
          {messages.map(msg => <motion.div key={msg.id} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-959" data-mp="20zmakxryp4b9">
              {msg.role === 'ai' ? <div className="flex gap-4 max-w-2xl" data-id="element-960" data-mp="1i9o42m3tbj9q">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-primary-indigo to-ai-purple flex items-center justify-center shadow-md mt-1" data-id="element-961" data-mp="1i8cweb78xrdc">
                    <SparklesIcon className="w-5 h-5 text-white" data-id="element-962" data-mp="1s1tu3qai7qma" />
                  </div>
                  <div className="space-y-3" data-id="element-963" data-mp="uk26u4703e4d">
                    <div className="bg-white rounded-2xl rounded-tl-none p-5 shadow-soft border border-slate-100" data-id="element-964" data-mp="zjyau3fp75jx">
                      <p className="text-slate-700 leading-relaxed" data-id="element-965" data-mp="318ubct24xc85">
                        {msg.content}
                      </p>
                    </div>

                    {/* Dynamic AI Content based on message type */}
                    {msg.type === 'greeting' && <div className="flex flex-wrap gap-2 pt-2" data-id="element-966" data-mp="3686zri8nmrcu">
                        <button onClick={() => handleAction('excel')} className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-medium text-slate-700 hover:border-primary-indigo hover:text-primary-indigo transition-colors shadow-sm" data-id="element-967" data-mp="366nflgjvnw5s">
                          <UploadCloudIcon className="w-4 h-4" data-id="element-968" data-mp="3oy6mhyh0vud3" /> Upload an
                          Excel sheet
                        </button>
                        <button onClick={() => handleAction('api')} className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-medium text-slate-700 hover:border-primary-indigo hover:text-primary-indigo transition-colors shadow-sm" data-id="element-969" data-mp="1zk0e5g1c5uxj">
                          <GlobeIcon className="w-4 h-4" data-id="element-970" data-mp="1auje3dyj9ok" /> Connect website /
                          API
                        </button>
                        <button onClick={() => handleAction('single')} className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-medium text-slate-700 hover:border-primary-indigo hover:text-primary-indigo transition-colors shadow-sm" data-id="element-971" data-mp="5ad3wy1v9iqv">
                          <PlusIcon className="w-4 h-4" data-id="element-972" data-mp="3ihwo500mqjqd" /> Add my first item
                        </button>
                      </div>}

                    {msg.type === 'upload_prompt' && <div className="pt-2" data-id="element-973" data-mp="1grcr5f9cx7e1">
                        <input type="file" accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel" className="hidden" ref={fileInputRef} onChange={handleFileUpload} data-id="element-974" data-mp="1goyyfd25g79n" />
                        <button onClick={() => fileInputRef.current?.click()} className="flex items-center gap-2 px-5 py-3 bg-primary-indigo text-white rounded-xl text-sm font-medium hover:bg-indigo-700 transition-colors shadow-sm" data-id="element-975" data-mp="2bpjx5haheziq">
                          <FileSpreadsheetIcon className="w-4 h-4" data-id="element-976" data-mp="2zkcyq9fml9x7" /> Select
                          File
                        </button>
                      </div>}

                    {msg.type === 'url_prompt' && <div className="pt-2 flex gap-2 max-w-md" data-id="element-977" data-mp="3nc1uy0vkiijf">
                        <div className="relative flex-1" data-id="element-978" data-mp="3n9p571jkj3vt">
                          <LinkIcon className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" data-id="element-979" data-mp="3unyf2v5svqzb" />
                          <input type="url" id="url-input" placeholder="https://yourwebsite.com" className="w-full pl-9 pr-3 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-primary-indigo/20 outline-none" onKeyDown={e => {
                    if (e.key === 'Enter') {
                      handleUrlSubmit(e.currentTarget.value);
                    }
                  }} data-id="element-980" data-mp="3heuwpe2qooc" />
                        </div>
                        <button onClick={() => {
                  const input = document.getElementById('url-input') as HTMLInputElement;
                  if (input && input.value) handleUrlSubmit(input.value);
                }} className="px-4 py-2.5 bg-primary-indigo text-white rounded-xl text-sm font-medium hover:bg-indigo-700 transition-colors shadow-sm" data-id="element-981" data-mp="106w9xuyy5n2p">
                          Connect
                        </button>
                      </div>}

                    {/* Vehicle image upload prompt */}
                    {msg.type === 'image_prompt' && <div className="pt-2 flex flex-wrap gap-2" data-id="element-982" data-mp="12pieq75idnsg">
                        <input type="file" accept="image/*" multiple className="hidden" ref={imageInputRef} onChange={handleVehicleImages} data-id="element-983" data-mp="1hhsoda8en5zg" />
                        <button onClick={() => imageInputRef.current?.click()} className="flex items-center gap-2 px-5 py-3 bg-primary-indigo text-white rounded-xl text-sm font-medium hover:bg-indigo-700 transition-colors shadow-sm" data-id="element-984" data-mp="jyrbzog10vn1">
                          <ImageIcon className="w-4 h-4" data-id="element-985" data-mp="2lo6m97hymke7" /> Upload photos
                        </button>
                        <button onClick={() => {
                  addMessage('user', 'Skip photos for now');
                  finishVehicleListing(false);
                }} className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-medium text-slate-700 hover:border-primary-indigo hover:text-primary-indigo transition-colors shadow-sm" data-id="element-986" data-mp="3eeoxwwwr1u38">
                          Skip for now
                        </button>
                      </div>}

                    {/* Single item draft preview */}
                    {msg.type === 'single_result' && msg.draft && <div className="space-y-4 pt-2" data-id="element-987" data-mp="14blff4wqnkmf">
                        <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm max-w-md" data-id="element-988" data-mp="148z5wzqaev1k">
                          <div className="flex items-center gap-2 text-xs font-bold text-primary-indigo uppercase tracking-wider mb-3" data-id="element-989" data-mp="van74af9oqy1">
                            <SparklesIcon className="w-3.5 h-3.5" data-id="element-990" data-mp="gkobaeb6prlq" /> AI Draft
                            Listing
                          </div>
                          <h4 className="font-bold text-dark-navy text-lg mb-1" data-id="element-991" data-mp="lek3t18oim9i">
                            {msg.draft.title}
                          </h4>
                          <div className="flex items-center gap-1.5 text-primary-indigo font-bold mb-3" data-id="element-992" data-mp="cl733d4digcv">
                            <TagIcon className="w-4 h-4" data-id="element-993" data-mp="3ttkbm9khqy5s" />
                            {msg.draft.price}
                          </div>
                          <p className="text-sm text-slate-600 leading-relaxed" data-id="element-994" data-mp="2nsqjolkmbpu">
                            {msg.draft.description}
                          </p>
                          <div className="mt-4 flex items-center gap-2 text-xs text-amber-600 font-medium" data-id="element-995" data-mp="3b5a5h44at2xn">
                            <AlertCircleIcon className="w-3.5 h-3.5" data-id="element-996" data-mp="3p5b7imwaugep" /> No photo
                            yet — add one for the best results.
                          </div>
                        </div>

                        <div className="flex flex-wrap gap-2" data-id="element-997" data-mp="1x7jbaimtwta">
                          <button onClick={() => handlePublishAction('Publish this listing now')} className="flex items-center gap-2 px-4 py-2 bg-primary-indigo text-white rounded-xl text-sm font-medium hover:bg-indigo-700 transition-colors shadow-sm" data-id="element-998" data-mp="3u3mgywiomowe">
                            <CheckCircle2Icon className="w-4 h-4" data-id="element-999" data-mp="gopw6zg73b8f" /> Publish now
                          </button>
                          <button onClick={() => handlePublishAction('Add a photo before publishing')} className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-medium text-slate-700 hover:border-primary-indigo hover:text-primary-indigo transition-colors shadow-sm" data-id="element-1000" data-mp="ns68hmvisirr">
                            <ImageIcon className="w-4 h-4" data-id="element-1001" data-mp="1bf4l29gir6ke" /> Add a photo
                          </button>
                          <button onClick={handleEditDraft} className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-medium text-slate-700 hover:border-primary-indigo hover:text-primary-indigo transition-colors shadow-sm" data-id="element-1002" data-mp="1gglfbm4hzus0">
                            <PencilIcon className="w-4 h-4" data-id="element-1003" data-mp="3fmz7kwzc1cmq" /> Edit details
                          </button>
                        </div>
                      </div>}

                    {/* Excel import summary with publish choices */}
                    {msg.type === 'excel_summary' && <div className="space-y-4 pt-2" data-id="element-1004" data-mp="30yvz9gxthour">
                        <div className="grid grid-cols-3 gap-3 max-w-lg" data-id="element-1005" data-mp="2g77n9opumy7">
                          <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm flex flex-col gap-1" data-id="element-1006" data-mp="2ffl0od3jzk2">
                            <div className="flex items-center gap-1.5 text-slate-500 font-medium text-xs" data-id="element-1007" data-mp="3onf9i20fgbph">
                              <CheckCircle2Icon className="w-3.5 h-3.5 text-primary-indigo" data-id="element-1008" data-mp="3jokrv3e74t6y" />{' '}
                              Cars added
                            </div>
                            <div className="text-2xl font-display font-bold text-dark-navy" data-id="element-1009" data-mp="2d65rljwy5rqa">
                              24
                            </div>
                          </div>
                          <div className="bg-white border border-green-100 rounded-xl p-4 shadow-sm flex flex-col gap-1" data-id="element-1010" data-mp="wnsjzipn7us">
                            <div className="flex items-center gap-1.5 text-green-600 font-medium text-xs" data-id="element-1011" data-mp="3n56w8m4b3uaj">
                              <ImageIcon className="w-3.5 h-3.5" data-id="element-1012" data-mp="3s40awhvbxrca" /> With photos
                            </div>
                            <div className="text-2xl font-display font-bold text-dark-navy" data-id="element-1013" data-mp="jnx3md84q4oh">
                              18
                            </div>
                          </div>
                          <div className="bg-white border border-amber-100 rounded-xl p-4 shadow-sm flex flex-col gap-1" data-id="element-1014" data-mp="9qiwj483is8u">
                            <div className="flex items-center gap-1.5 text-amber-600 font-medium text-xs" data-id="element-1015" data-mp="eoke1kpviji7">
                              <AlertCircleIcon className="w-3.5 h-3.5" data-id="element-1016" data-mp="2ufgiie6l6uey" /> No
                              photos
                            </div>
                            <div className="text-2xl font-display font-bold text-dark-navy" data-id="element-1017" data-mp="2zd8831jre4fz">
                              6
                            </div>
                          </div>
                        </div>

                        <p className="text-sm text-slate-500 leading-relaxed max-w-lg" data-id="element-1018" data-mp="1qie1mmrh340m">
                          The 6 cars without photos can still be found in your
                          inventory as drafts — you can add photos to them
                          anytime to publish them.
                        </p>

                        <div className="flex flex-wrap gap-2" data-id="element-1019" data-mp="tt6i1kaez4zc">
                          <button onClick={() => handlePublishAction('Publish all 24 — with and without photos')} className="flex items-center gap-2 px-4 py-2 bg-primary-indigo text-white rounded-xl text-sm font-medium hover:bg-indigo-700 transition-colors shadow-sm" data-id="element-1020" data-mp="17js23cbltkyt">
                            <SparklesIcon className="w-4 h-4" data-id="element-1021" data-mp="2a4kt30gqoq1m" /> Publish all 24
                            (with & without photos)
                          </button>
                          <button onClick={() => handlePublishAction('Publish only the 18 cars with photos')} className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-medium text-slate-700 hover:border-primary-indigo hover:text-primary-indigo transition-colors shadow-sm" data-id="element-1022" data-mp="2jxi64r2ypmsu">
                            Publish only the 18 with photos
                          </button>
                        </div>
                      </div>}

                    {(msg.type === 'upload_result' || msg.type === 'url_result') && <div className="space-y-4 pt-2" data-id="element-1023" data-mp="15vkf2f689nt1">
                        <div className="grid grid-cols-2 gap-3" data-id="element-1024" data-mp="10y10tf9j5b13">
                          <div className="bg-white border border-green-100 rounded-xl p-4 shadow-sm flex flex-col gap-1" data-id="element-1025" data-mp="10zvm4uopr55p">
                            <div className="flex items-center gap-2 text-green-600 font-medium text-sm" data-id="element-1026" data-mp="r7z98dhyxahf">
                              <CheckCircle2Icon className="w-4 h-4" data-id="element-1027" data-mp="m8mpi1me8jj2" />{' '}
                              Successfully Added
                            </div>
                            <div className="text-2xl font-display font-bold text-dark-navy" data-id="element-1028" data-mp="i5zs7l6ye6eh">
                              42
                            </div>
                          </div>
                          <div className="bg-white border border-amber-100 rounded-xl p-4 shadow-sm flex flex-col gap-1" data-id="element-1029" data-mp="8aob4w82on3k">
                            <div className="flex items-center gap-2 text-amber-600 font-medium text-sm" data-id="element-1030" data-mp="27k4v8u3ym8a3">
                              <AlertCircleIcon className="w-4 h-4" data-id="element-1031" data-mp="22lbix50esnbi" /> Needs
                              Pictures
                            </div>
                            <div className="text-2xl font-display font-bold text-dark-navy" data-id="element-1032" data-mp="1gftuke95kxmu">
                              12
                            </div>
                          </div>
                        </div>

                        <div className="flex flex-wrap gap-2" data-id="element-1033" data-mp="16phbyrkwfejc">
                          <button onClick={() => handlePublishAction('Publish 30 ready items now')} className="flex items-center gap-2 px-4 py-2 bg-primary-indigo text-white rounded-xl text-sm font-medium hover:bg-indigo-700 transition-colors shadow-sm" data-id="element-1034" data-mp="sqrcx6eir4sc">
                            <SparklesIcon className="w-4 h-4" data-id="element-1035" data-mp="2d6mfpqay1fvh" /> Publish 30
                            ready items
                          </button>
                          <button onClick={() => handlePublishAction('Publish all without pictures')} className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-medium text-slate-700 hover:border-primary-indigo hover:text-primary-indigo transition-colors shadow-sm" data-id="element-1036" data-mp="2i5fq39uxoqrf">
                            Publish all without pictures
                          </button>
                          <button onClick={() => handlePublishAction('I will add pictures first')} className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-medium text-slate-700 hover:border-primary-indigo hover:text-primary-indigo transition-colors shadow-sm" data-id="element-1037" data-mp="l9ul3p7uzeft">
                            <ImageIcon className="w-4 h-4" data-id="element-1038" data-mp="24y9tudivxyta" /> Add pictures
                          </button>
                        </div>
                      </div>}
                  </div>
                </div> : <div className="bg-primary-indigo text-white rounded-2xl rounded-tr-none p-4 max-w-md shadow-soft" data-id="element-1039" data-mp="1w3lpcpl4wl5j">
                  <p className="leading-relaxed" data-id="element-1040" data-mp="1m7imfhigkihj">{msg.content}</p>
                </div>}
            </motion.div>)}

          {isTyping && <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} className="flex gap-4 max-w-2xl" data-id="element-1041" data-mp="1h74ahodqkoe8">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-primary-indigo to-ai-purple flex items-center justify-center shadow-md mt-1" data-id="element-1042" data-mp="37jttuwk4m6ju">
                <SparklesIcon className="w-5 h-5 text-white" data-id="element-1043" data-mp="3a5koujvuixti" />
              </div>
              <div className="bg-white rounded-2xl rounded-tl-none p-5 shadow-soft border border-slate-100 flex items-center gap-1.5 h-[60px]" data-id="element-1044" data-mp="3a3gm459oo4ut">
                <div className="w-2 h-2 bg-slate-300 rounded-full animate-bounce" style={{
              animationDelay: '0ms'
            }} data-id="element-1045" data-mp="3jw5nu8ffm4cy" />
                <div className="w-2 h-2 bg-slate-300 rounded-full animate-bounce" style={{
              animationDelay: '150ms'
            }} data-id="element-1046" data-mp="2r7qk4i1r38gx" />
                <div className="w-2 h-2 bg-slate-300 rounded-full animate-bounce" style={{
              animationDelay: '300ms'
            }} data-id="element-1047" data-mp="3131rugeqrff6" />
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>

      {/* Composer — pinned, always visible */}
      <div className="flex-shrink-0 p-4 bg-white border-t border-slate-100" data-id="element-1048" data-mp="2hgd81h5xnzbj">
        <div className="max-w-4xl mx-auto relative flex items-center" data-id="element-1049" data-mp="2hig7ssww16tg">
          <input type="text" value={inputValue} onChange={e => setInputValue(e.target.value)} onKeyDown={e => {
          if (e.key === 'Enter') handleSend();
        }} placeholder={flowStep === 'awaiting_vehicle_details' ? 'Make, model, year, mileage…' : flowStep === 'awaiting_single_desc' ? 'Describe your item…' : 'Ask Talab a question or describe what you want to add...'} className="w-full pl-5 pr-14 py-3.5 bg-slate-50 border border-slate-200 rounded-full text-slate-700 placeholder-slate-400 focus:outline-none focus:border-primary-indigo focus:ring-1 focus:ring-primary-indigo transition-shadow" data-id="element-1050" data-mp="2ch0op6e9tadp" />
          <button onClick={handleSend} disabled={!inputValue.trim()} className="absolute right-2 w-10 h-10 flex items-center justify-center bg-primary-indigo text-white rounded-full hover:bg-indigo-700 disabled:opacity-50 disabled:hover:bg-primary-indigo transition-colors" data-id="element-1051" data-mp="1a2ifwhg71a2z">
            <ArrowUpIcon className="w-5 h-5" data-id="element-1052" data-mp="3cnvwzul27pja" />
          </button>
        </div>
      </div>
    </div>;
}